import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import { AnalysisResult } from '../types'; // Add this import

interface NexusRule {
  state: string;
  threshold_amount: number;
  threshold_count: number;
  period_type: string;
}

interface NexusRuleData {
  State: string;
  Period: string;
  'Dollar Amt': number | null;
  'Transaction Count': number | null;
  'Considers Wholesale'?: boolean;
  'Considers Exempt Physical Goods'?: boolean;
  'Considers Marketplace Transactions'?: boolean;
  'Considers $0.00 Transactions'?: boolean;
}

interface NexusRulesEditorProps {
  onClose: () => void;
}

const NexusRulesEditor: React.FC<NexusRulesEditorProps> = ({ onClose }) => {
  const [rules, setRules] = useState<NexusRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editRule, setEditRule] = useState<NexusRule | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [_analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/nexus');
      
      console.log('API response:', response.data);
      
      if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        throw new Error('Received HTML instead of JSON. API server may not be running.');
      }
      
      // Get rules from response and ensure it's an array
      let rulesData: NexusRuleData[] = [];
      if (response.data && response.data.rules) {
        rulesData = response.data.rules;
      } else if (Array.isArray(response.data)) {
        rulesData = response.data;
      }
      
      console.log('Rules data:', rulesData);
      
      // Transform the nexus.csv data format
      const formattedRules = rulesData.map((rule: NexusRuleData) => ({
        state: rule.State,
        threshold_amount: rule['Dollar Amt'] || 0,
        threshold_count: rule['Transaction Count'] !== null ? rule['Transaction Count'] : 0,
        period_type: rule.Period
      }));
      
      setRules(formattedRules);
    } catch (err) {
      console.error('Error fetching nexus rules:', err);
      setError('Failed to load nexus rules: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const resetRules = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Sending reset request to /api/nexus/reset');
      const response = await axios.post('/api/nexus/reset');
      console.log('Reset response:', response);
      
      if (response.data && response.data.rules) {
        // Transform the reset data
        const formattedRules = response.data.rules.map((rule: NexusRuleData) => ({
          state: rule.State,
          threshold_amount: rule['Dollar Amt'] || 0,
          threshold_count: rule['Transaction Count'] !== null ? rule['Transaction Count'] : 0,
          period_type: rule.Period
        }));
        
        setRules(formattedRules);
      } else {
        console.log('No rules found in response, using fetchRules instead');
        // Fallback to just refetching if the reset endpoint doesn't work as expected
        await fetchRules();
      }
    } catch (err) {
      console.error('Error resetting nexus rules:', err);
      setError('Failed to reset nexus rules: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleResetFallback = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First try the API endpoint
      try {
        const response = await axios.post('/api/nexus/reset');
        if (response.data && response.data.rules) {
          const formattedRules = response.data.rules.map((rule: NexusRuleData) => ({
            state: rule.State,
            threshold_amount: rule['Dollar Amt'] || 0,
            threshold_count: rule['Transaction Count'] !== null ? rule['Transaction Count'] : 0,
            period_type: rule.Period
          }));
          
          setRules(formattedRules);
          return; // If successful, exit here
        }
      } catch (resetErr) {
        console.error('Reset API error, trying fallback:', resetErr);
      }
      
      // If API fails, try to get the original nexus rules
      await fetchRules();
      
    } catch (err) {
      console.error('Error with reset fallback:', err);
      setError('Failed to reset nexus rules: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (rule: NexusRule) => {
    setEditRule({ ...rule });
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setEditRule(null);
  };

  const handleSaveRule = async () => {
    if (!editRule) return;

    try {
      setSaving(true);
      
      // Use the correct endpoint with the expected payload format
      await axios.post('/api/nexus/update', {
        state: editRule.state,
        type: 'dollar',
        value: editRule.threshold_amount
      });
      
      // Make a separate call for the count threshold
      await axios.post('/api/nexus/update', {
        state: editRule.state,
        type: 'count',
        value: editRule.threshold_count
      });
      
      // Update local state
      setRules(prevRules => 
        prevRules.map(rule => 
          rule.state === editRule.state ? editRule : rule
        )
      );
      
      setEditDialogOpen(false);
      setEditRule(null);
    } catch (err) {
      console.error('Error saving rule:', err);
      setError('Failed to save nexus rule changes');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof NexusRule, value: string | number) => {
    if (!editRule) return;
    
    // Handle empty strings for numeric fields
    const processedValue = value === '' && (field === 'threshold_amount' || field === 'threshold_count') 
      ? 0 
      : value;
    
    setEditRule({
      ...editRule,
      [field]: processedValue
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h2">
          Nexus Rules
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CloseIcon />}
          onClick={onClose}
        >
          Close
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Typography variant="body1" fontWeight="medium">
          All rules are based on the most current 2024-2025 nexus legislations for each state. You may edit thresholds based on your needs if reviewing data from prior periods.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1">
            These are the current nexus rules used for analysis. You can edit the threshold values for each state.
          </Typography>
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RestoreIcon />}
              onClick={handleResetFallback}
              sx={{ ml: 1 }}
            >
              Reset to 2024-2025
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          If you've changed any economic nexus threshold data, use this feature to reset to application defaults.
        </Typography>
        
        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Amount Threshold</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Count Threshold</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Period Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.state}>
                  <TableCell>{rule.state}</TableCell>
                  <TableCell>${rule.threshold_amount.toLocaleString()}</TableCell>
                  <TableCell>{rule.threshold_count.toLocaleString()}</TableCell>
                  <TableCell>{rule.period_type}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditClick(rule)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Nexus Rule for {editRule?.state}</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount Threshold"
            type="number"
            fullWidth
            margin="normal"
            value={editRule?.threshold_amount ?? ''}
            onChange={(e) => handleChange('threshold_amount', e.target.value === '' ? '' : parseFloat(e.target.value))}
            InputProps={{
              startAdornment: <span>$</span>
            }}
          />
          <TextField
            label="Count Threshold"
            type="number"
            fullWidth
            margin="normal"
            value={editRule?.threshold_count ?? ''}
            onChange={(e) => handleChange('threshold_count', e.target.value === '' ? '' : parseInt(e.target.value))}
          />
          <TextField
            label="Period Type"
            fullWidth
            margin="normal"
            value={editRule?.period_type || ''}
            onChange={(e) => handleChange('period_type', e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveRule}
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NexusRulesEditor;